import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Check, Gift, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export default function DailyReward({ refresh }) {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const openLater = () => setOpen(true);
    Promise.all([api.get("/rewards/daily"), api.get("/gifts/unseen")]).then(([{ data }, gifts]) => {
      setSt(data);
      if (!data.can_claim || sessionStorage.getItem("daily-seen")) return;
      // Wait for the gift popup to close first so two dialogs never stack.
      if (gifts.data.length) window.addEventListener("gift-popup-closed", openLater, { once: true });
      else setOpen(true);
    }).catch(() => {});
    return () => window.removeEventListener("gift-popup-closed", openLater);
  }, []);

  const close = () => { sessionStorage.setItem("daily-seen", "1"); setOpen(false); };
  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rewards/daily/claim");
      toast.success(`Day ${data.day} reward: +${data.credits} credits`);
      setSt((s) => ({ ...s, can_claim: false, claimed_today: true }));
      await refresh();
      close();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!st) return null;

  return (
    <>
      <button data-testid="daily-reward-open-btn" onClick={() => setOpen(true)}
        className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg border text-sm transition-colors ${st.can_claim ? "border-amber-400/70 text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 nft-glow" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
        <Gift className="w-4 h-4" />{st.can_claim ? `Claim day ${st.day} reward` : `Daily streak: day ${st.day} of 7`}
      </button>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="daily-reward-dialog" className="glass max-w-lg">
          <DialogHeader>
            <p className="text-[11px] font-mono-game text-amber-300">DAILY LOGIN STREAK</p>
            <DialogTitle className="font-display text-3xl text-white">{st.can_claim ? `Day ${st.day} reward` : "Come back tomorrow"}</DialogTitle>
            <DialogDescription className="text-slate-300">
              {st.streak_broken ? "Your streak was broken, so it starts again from day 1. " : ""}Log in every day for bigger rewards. After day 7 the streak starts over at day 1. Miss a day and it resets.
            </DialogDescription>
          </DialogHeader>
          <div data-testid="daily-holder-bonus" className={`rounded-xl border p-3 text-sm ${st.bonus_label ? "border-amber-400/60 bg-amber-500/10 nft-glow text-amber-100" : "border-slate-700 bg-slate-900/60 text-slate-300"}`}>
            {st.bonus_label
              ? <p className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-300" /><span><b>x{st.multiplier} holder bonus</b> from {st.bonus_label}</span></p>
              : <p>Hold an NFT to multiply these rewards:</p>}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {st.tiers.map((t) => (
                <span key={t.key} className={`px-2 py-0.5 rounded-full text-[11px] font-mono-game ${t.label === st.bonus_label ? "bg-amber-400 text-slate-950" : "bg-slate-800 text-slate-400"}`}>
                  {t.key === "pack" ? "All three" : t.label.replace("Metaland Wolfpack Exclusive", "Exclusive").replace("Metaland ", "")} x{t.multiplier}
                </span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2 py-3">
            {st.rewards.map((c, i) => {
              const day = i + 1;
              const done = st.claimed_today ? day <= st.day : day < st.day;
              const next = st.can_claim && day === st.day;
              return (
                <div key={day} data-testid={`daily-day-${day}`} data-state={done ? "claimed" : next ? "next" : "upcoming"}
                  className={`rounded-xl border p-1.5 sm:p-2 text-center ${next ? "border-amber-400 bg-amber-500/15 nft-glow" : done ? "border-emerald-600/50 bg-emerald-950/40" : "border-slate-700 bg-slate-900/60"} ${day === 7 ? "ring-1 ring-fuchsia-500/50" : ""}`}>
                  <p className="text-[9px] sm:text-[10px] font-mono-game text-slate-400">DAY {day}</p>
                  {done ? <Check className="w-4 h-4 mx-auto my-1 text-emerald-300" /> : <Coins className={`w-4 h-4 mx-auto my-1 ${day === 7 ? "text-fuchsia-300" : "text-amber-300"}`} />}
                  <p className="font-mono-game text-xs sm:text-sm text-white">{c.toLocaleString()}</p>
                  {st.multiplier > 1 && <p className="font-mono-game text-[9px] text-slate-500 line-through">{st.base_rewards[i]}</p>}
                </div>
              );
            })}
          </div>
          {st.can_claim
            ? <button data-testid="daily-claim-btn" disabled={busy} onClick={claim} className="ember-btn w-full py-3 rounded-lg text-sm">CLAIM +{st.rewards[st.day - 1].toLocaleString()} CREDITS</button>
            : <button data-testid="daily-close-btn" onClick={close} className="w-full py-3 rounded-lg border border-slate-600 text-sm">Got it</button>}
        </DialogContent>
      </Dialog>
    </>
  );
}
